import { defineStore } from 'pinia';
import { Services } from 'src/services/Services';
import { Api } from 'src/services/api/Api';
import { useNewBookingStore } from 'src/stores/newBookingStore';
import { usePropertyStore } from 'src/stores/propertyStore';
import {
  AvailabilityCacheDTO,
  AvailabilityCacheRateDTO,
} from 'src/services/api/data-contracts';
import { IAlternativeStay } from 'src/types/createBooking';
import { date } from 'quasar';
const { addToDate, formatDate, isBetweenDates, extractDate } = date;
import useDateHelperUtils from 'src/composables/dateHelperUtils.ts';

const apiService = Services.getService(Api);
const dateHelperUtils = useDateHelperUtils();

function getMinimumLengthOfStayRate(
  rates: Array<AvailabilityCacheRateDTO>,
  numberOfNights: number,
): number {
  let rate = null;
  const minLOS = rates.filter((y) => {
    return y.MinLengthOfStay == numberOfNights;
  });
  if (minLOS.length > 0) {
    rate = minLOS[0].MinimumRate;
  } else {
    const nullLos = rates.filter((y) => {
      return y.MinLengthOfStay == null;
    });
    if (nullLos.length > 0) {
      rate = nullLos[0].MinimumRate;
    }
  }
  return rate;
}

function getMinimumLengthOfStay(
  rates: Array<AvailabilityCacheRateDTO>,
): number | null {
  let minLengthOfStay = null;
  if (rates.length > 0) {
    const sortedRates = rates.sort((a, b) => {
      return a.MinLengthOfStay > b.MinLengthOfStay ? 1 : -1;
    });
    minLengthOfStay = sortedRates[0].MinLengthOfStay;
  }
  return minLengthOfStay;
}

type AvailabilityCacheHash = {
  [date: string]: Array<AvailabilityCacheDTO>;
};

export const useAvailabilityStore = defineStore('availabilityStore', {
  state: () => ({
    fromDate: null as string,
    numberOfNights: 1 as number,
    fromDateMinimumLengthOfStay: null as number | null,
    availabilityCache: [] as Array<AvailabilityCacheDTO>,
    availabilityCacheHash: {} as AvailabilityCacheHash,
    alternativeStays: [] as Array<IAlternativeStay>,
    disabledDates: [] as Array<string>,
  }),
  getters: {
    hasAlternativeStays(): boolean {
      return this.alternativeStays.length > 0;
    },
  },
  actions: {
    __clearCache() {
      this.availabilityCache.splice(0); // maintain reactivity
      this.alternativeStays.splice(0);
      this.fromDate = null;
      this.fromDateMinimumLengthOfStay = null;
    },

    clearAlternativeStays() {
      this.alternativeStays.splice(0);
      this.fromDate = null;
      this.fromDateMinimumLengthOfStay = null;
      this.numberOfNights = null;
    },

    calculateDisabledDays() {
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Normalize to start of day

      // Calculate end date once
      const endDate: Date = addToDate(today, { year: 3, month: 1 });
      endDate.setHours(0, 0, 0, 0);

      // Pre-allocate array with estimated size
      const estimatedDays = Math.ceil(
        (endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
      );
      const days: string[] = new Array(estimatedDays);

      let currentDate = today;
      let dayIndex = 0;

      // Use timestamp comparison instead of Date object comparison
      const endTimestamp = endDate.getTime();

      while (currentDate.getTime() <= endTimestamp) {
        const formattedDate = formatDate(new Date(currentDate), 'YYYY-MM-DD');
        const avail = this.isDateAvailable(formattedDate);
        const isInRange = isBetweenDates(formattedDate, today, endDate, {
          inclusiveFrom: true,
          inclusiveTo: true,
          onlyDate: true,
        });

        if (!avail || !isInRange) {
          days[dayIndex++] = formattedDate;
        }

        // Use timestamp arithmetic instead of setDate
        currentDate = new Date(currentDate.getTime() + 86400000); // 86400000 = 24 * 60 * 60 * 1000
      }

      // Trim array to actual size
      this.disabledDates = days.slice(0, dayIndex);
    },

    isDateAvailable(dateValue: string): boolean {
      let isAvailable = true;
      const item = this.availabilityCacheHash[dateValue];
      if (item != null && item.length > 0) {
        isAvailable = item[0].AvailableRooms > 0;
      }
      return isAvailable;
    },

    getDefaultMinLengthOfStay(dateValue: string): number | null {
      /// Get the default Min LOS - null is the best, then followed by a number
      let minLengthOfStay = null; // default to 1
      const d = formatDate(extractDate(dateValue, 'YYYY/MM/DD'), 'YYYY-MM-DD');

      if (this.availabilityCache.length > 0) {
        const dateValueData = this.availabilityCache.filter(
          (item: AvailabilityCacheDTO) => {
            return dateHelperUtils.isSameDate(item.DateOfStay, d);
          },
        );

        if (dateValueData.length > 0) {
          const first = dateValueData[0];
          minLengthOfStay = getMinimumLengthOfStay(first.Rates);
        }
      }
      return minLengthOfStay;
    },

    generateAlternativeStays() {
      this.clearAlternativeStays();

      const newBookingStore = useNewBookingStore();

      this.fromDate = newBookingStore.fromDate;
      this.numberOfNights = newBookingStore.numberOfNights;

      let numberOfNightsForStay = newBookingStore.numberOfNights;

      let numberOfRooms = newBookingStore.rooms.length;
      if (numberOfRooms == 0) {
        // no guest selection yet
        numberOfRooms = 1;
      }

      const fromDateData = this.availabilityCacheHash[this.fromDate];
      if (fromDateData != null && fromDateData.length > 0) {
        const item = fromDateData[0];
        this.fromDateMinimumLengthOfStay = getMinimumLengthOfStay(item.Rates);

        if (this.fromDateMinimumLengthOfStay != null) {
          numberOfNightsForStay = this.fromDateMinimumLengthOfStay;
        }
      }

      for (let index = 0; index < this.availabilityCache.length; index++) {
        const item = this.availabilityCache[index];

        if (
          item.AvailableRooms >= numberOfRooms &&
          dateHelperUtils.isSameOrAfterDate(item.DateOfStay, this.fromDate)
        ) {
          let isAvailable = true;
          let totalRate = null;
          const minRate = getMinimumLengthOfStayRate(
            item.Rates,
            numberOfNightsForStay,
          );
          if (minRate != null) {
            totalRate += minRate;

            for (let x = 1; x < numberOfNightsForStay; x++) {
              const nextElement = this.availabilityCache[index + x];

              if (nextElement && nextElement.AvailableRooms >= numberOfRooms) {
                const nextElementMinRate = getMinimumLengthOfStayRate(
                  nextElement.Rates,
                  numberOfNightsForStay,
                );
                totalRate += nextElementMinRate;
              } else {
                isAvailable = false;
                totalRate = null;
                break;
              }
            }

            if (isAvailable) {
              this.alternativeStays.push({
                fromDate: item.DateOfStay,
                toDate: dateHelperUtils.calculateToDate(
                  item.DateOfStay,
                  numberOfNightsForStay,
                ),
                fromDateDisplay: dateHelperUtils.formatDateDisplay(
                  item.DateOfStay,
                ),
                numberOfNights: numberOfNightsForStay,
                numberOfNightsDisplay:
                  dateHelperUtils.formatNumberOfNightsDisplay(
                    numberOfNightsForStay,
                  ),
                totalCost: totalRate,
              } as IAlternativeStay);
            }

            if (this.alternativeStays.length >= 10) {
              break;
            }
          }
        }
      }
    },

    getAvailabilityCache() {
      const propertryStore = usePropertyStore();

      if (propertryStore.propertyId) {
        const fromDate = formatDate(new Date(), 'YYYY-MM-DD');
        const toDate = formatDate(
          addToDate(fromDate, { months: 36 }),
          'YYYY-MM-DD',
        );

        const query = {
          propertyId: propertryStore.propertyId,
          fromDate: fromDate,
          toDate: toDate,
          languageCode: 'en',
        };

        const availabilityCachePromise = apiService.availabilityGet(query);
        availabilityCachePromise.then((availabilityCacheResponse) => {
          this.availabilityCache = availabilityCacheResponse.data.map((t) => {
            t.DateOfStay = formatDate(
              extractDate(t.DateOfStay, 'YYYY-MM-DD'),
              'YYYY-MM-DD',
            );

            const mappedItem = this.availabilityCacheHash[t.DateOfStay];
            if (mappedItem == null) {
              this.availabilityCacheHash[t.DateOfStay] = [t];
            } else {
              this.availabilityCacheHash[t.DateOfStay].push(t);
            }

            return t;
          });

          // delay this operation for a bit to allow other things to complete...
          setTimeout(() => {
            this.calculateDisabledDays();
          }, 3000);
        });
      }
    },
  },
});
